import React, { useState, useEffect } from 'react';
import {
    Box,
    FormControl,
    Switch,
    HStack,
    VStack,
    Select,
    CheckIcon,
} from 'native-base';
import {
    useUpdateMember,
    useAppSettings,
    useI18n,
    useSpringBigStore,
    useTheme,
    useUpdatePreferredSendingMethod, useGetMember,
} from '@springbig/shared';
import Alert from '~/shared/Alert';
import { Caption } from '~/shared/Typography';
import { useAuthorization } from '~/hooks/auth';
import Loading from '~/shared/Loading';
import { Panel, Button, Section } from '~/shared/Theme';
import { SpIcon } from '~/shared/Icon';
import { faAngleDown } from '@fortawesome/free-solid-svg-icons';

export type SettingFormData = {
    allowed_sms: boolean;
    allowed_email: boolean;
    allowed_loyalty: boolean;
    allow_push_notifications: boolean;
};

const SettingsScreen = () => {

    const [isDisabled, setIsDisabled] = useState(true);

    const { t } = useI18n();
    const { member, walletId } = useSpringBigStore();

    const {
        refetch: refetchMember,
        isLoading: isLoadingMember,
    } = useGetMember();

    const {
        mutate: updatePreferredSendingMethod,
        isLoading: isLoadingUpdatePreferredMethod,
    } = useUpdatePreferredSendingMethod();

    const sendingOptions = [
        { label: 'Email', value: 'email' },
        { label: 'Push Notifications', value: 'push' },
        { label: 'Text Messages', value: 'sms' },
    ];

    const preferredMethod = member?.preferred_sending_method?.[0] || 'sms';
    const preferredOption = sendingOptions.find(option => option.value === preferredMethod);

    const onUpdatePreferredSendingMethod = (value: 'email' | 'sms' | 'push'): void => {

        setIsLoading(true);
        updatePreferredSendingMethod({
            member_id: walletId,
            preferred_method: value,
        }, {
            onSuccess: () => refetchMember(),
            onSettled: () => {

                setIsLoading(false);
                setShowPreferredSelect(false);
            }
        });
    };

    const { mutate: updateMember } = useUpdateMember();

    const [isLoading, setIsLoading] =
        useState(isLoadingMember || isLoadingUpdatePreferredMethod);

    const [showPreferredSelect, setShowPreferredSelect] =
        useState<boolean>(false);

    const {
        primary,
        field_background,
        field_color,
        field_border,
        field_border_size,
        radius_sm,
        card,
    } = useTheme();

    const [formData, setFormData] =
        useState<SettingFormData>({
            allowed_sms: false,
            allowed_email: false,
            allowed_loyalty: false,
            allow_push_notifications: false
        });

    const [showAlert, setShowAlert] =
        useState<{
            status: boolean,
            id: 'notification' | 'closeNotification' | null,
            title: string,
            text: string,
            type?: string,
            disclaimer?: string,
            disable_signature?: boolean,
            opt_state?: 'opt_in' | 'opt_out';
        }>({ status: false, id: null, title: '', text: '', type: '', opt_state: undefined });

    const [signature, setSignature] = useState<string>();
    const { merchantOrg, account, loyalty_wallet_opt_in } = useAppSettings();

    const { isLoadingAuth } = useAuthorization();

    useEffect(() => {

        if (member) {
            setFormData({
                allowed_sms: !!member.allowed_sms,
                allowed_email: !!member.allowed_email,
                allowed_loyalty: !!member.allowed_loyalty,
                allow_push_notifications: !!member.allow_push_notifications
            });
        }
    }, [member]);

    const handleChanges = (value: boolean, type: 'sms' | 'push_notification' | 'email' | 'loyalty') => {

        let title;
        let text;
        let messageType;
        let disclaimer;
        let disable_signature;
        let opt_state;

        switch (type) {
        case 'sms':
            messageType = 'messaging';
            disclaimer = t('settings.sms_opt_in_disclaimer', [merchantOrg]);
            title = value ? 'Opt out of text marketing' : 'Opt in to text marketing';
            opt_state = value ? 'opt_out' : 'opt_in';
            text = 'Are you sure you want to opt out of receiving sms notifications?';
            disable_signature = !!account?.disable_signature?.sms;
            break;
        case 'push_notification':
            messageType = 'messaging';
            disclaimer = '';
            title = value ? 'Opt out of push notification' : 'Opt in to push notification';
            opt_state = value ? 'opt_out' : 'opt_in';
            text = 'Are you sure you want to opt out of receiving push notifications?';
            break;
        case 'email':
            messageType = 'messaging';
            disclaimer = t('settings.email_opt_in_disclaimer', [merchantOrg]);
            title = value ? 'Opt out of email marketing' : 'Opt in to email marketing';
            opt_state = value ? 'opt_out' : 'opt_in';
            text = 'Are you sure you want to opt out of receiving email notifications?';
            disable_signature = !!account?.disable_signature?.email;
            break;
        case 'loyalty':
            messageType = 'loyalty program';
            disclaimer = '';
            title = value ? 'Opt out of loyalty program' : 'Opt in to loyalty program';
            opt_state = value ? 'opt_out' : 'opt_in';
            text = 'Are you sure you want to opt out of the loyalty program?';
            disable_signature = !!account?.disable_signature?.loyalty;
        }

        if (!value) {
            setShowAlert({ status: true, id: 'notification', title, text, type: messageType, disclaimer, disable_signature, opt_state });
        }
        else {
            setShowAlert({ status: true, id: 'closeNotification', title, text, type: messageType, disclaimer, disable_signature, opt_state });
        }
    };

    const submitFormData = () => {

        updateMember({
            member: {
                allowed_sms: formData.allowed_sms,
                allowed_email: formData.allowed_email,
                allowed_loyalty: formData.allowed_loyalty,
            },
            signature
        });
    };

    const handleCancelConditions = () => {

        if (!member) {
            return;
        }

        setShowAlert({ status: false, id: null, title: '', text: '', type: '' });
        setFormData({
            allowed_sms: !!member.allowed_sms,
            allowed_email: !!member.allowed_email,
            allowed_loyalty: !!member.allowed_loyalty,
            allow_push_notifications: !!member.allow_push_notifications
        });
    };

    const handleAcceptConditions = () => {

        setShowAlert({ status: false, id: null, title: '', text: '', type: '' });
    };

    if (isLoadingAuth) {
        return <Loading isLoading={isLoadingAuth} />;
    }

    type Form = Array<{
        enabled: boolean;
        type: 'sms' | 'push_notification' | 'email' | 'loyalty';
        title: string;
        subtitle: string;
        variable: string;
    }>;

    const form: Form = [
        {
            enabled: true,
            type: 'sms',
            title: 'Text Marketing Communications',
            subtitle: `Allow ${member?.merchant_name} to send me marketing communication via text`,
            variable: 'allowed_sms',
        },
        {
            enabled: !account?.notification?.hideEmailToggle,
            type: 'email',
            title: 'Email Marketing Communications',
            subtitle: `Allow ${member?.merchant_name} to send me marketing communication via email`,
            variable: 'allowed_email',
        },
        {
            enabled: !!loyalty_wallet_opt_in && !!member?.manual_loyalty,
            type: 'loyalty',
            title: 'Loyalty Program',
            subtitle: 'Allow app to track my points for loyalty purposes',
            variable: 'allowed_loyalty',
        },
    ];

    return (
        <Section>
            <Panel space={8}>
                <HStack justifyContent={'space-between'} alignItems={'center'}>
                    <Caption text='settings.header_title' bold />
                    { isDisabled ?
                        <Button
                            text={'general.edit'}
                            modifier={'small'}
                            onPress={() => setIsDisabled(false)}
                        />
                        :
                        <Button
                            text={'general.save'}
                            modifier={'small'}
                            onPress={() => {

                                setIsDisabled(true);
                                submitFormData();
                            }}
                        />
                    }
                </HStack>
                <FormControl isDisabled={isDisabled}>
                    <VStack space={8}>
                        { form.map((checkbox, index) => {

                            if (checkbox.enabled) {
                                return (
                                    <HStack key={index} justifyContent={'space-between'} alignItems={'center'}>
                                        <Box flex={1}>
                                            <Caption>{ checkbox.title }</Caption>
                                            <Caption opacity={.5}>{ checkbox.subtitle }</Caption>
                                        </Box>
                                        <Switch
                                            onTrackColor={primary}
                                            offTrackColor={primary?.slice(0, 7) + '40'}
                                            _hover={{
                                                onTrackColor: primary?.slice(0, 7) + '40',
                                                offTrackColor: primary,
                                            }}
                                            isChecked={formData[checkbox.variable]}
                                            onValueChange={() => {

                                                handleChanges(!!formData[checkbox.variable], checkbox.type);
                                                setFormData((prevState) => ({ ...prevState, [checkbox.variable]: !prevState[checkbox.variable] }));
                                            }}
                                        />
                                    </HStack>
                                );
                            }
                        })}
                    </VStack>
                </FormControl>

                { showPreferredSelect ?
                    <>
                        <Caption
                            text={'general.preferred_method'}
                            bold
                        />
                        <Select
                            opacity={isLoading ? .5 : 1}
                            selectedValue={member?.preferred_sending_method && member?.preferred_sending_method.length > 0 && member?.preferred_sending_method[0]}
                            onValueChange={(itemValue: 'email' | 'sms' | 'push') => {

                                onUpdatePreferredSendingMethod(itemValue);
                            }}
                            minWidth={90}
                            h={'40px'}
                            fontSize={14}
                            _actionSheetContent={{
                                bg: card,
                            }}
                            color={field_color}
                            bg={field_background}
                            borderColor={field_border}
                            borderWidth={field_border_size}
                            rounded={radius_sm}
                            dropdownCloseIcon={
                                <Box px={2}>
                                    <SpIcon
                                        color={primary}
                                        icon={faAngleDown}
                                    />
                                </Box>
                            }
                            _selectedItem={{
                                endIcon: <CheckIcon ml={'auto'} alignSelf={'center'} size={5} color={primary} />
                            }}
                        >
                            { sendingOptions.map(({ label, value }, index) => (

                                <Select.Item
                                    key={`${value}-${index}`}
                                    label={label}
                                    value={value}
                                    bg={field_background}
                                    borderColor={field_border}
                                    borderWidth={field_border_size}
                                    rounded={radius_sm}
                                    _text={{ color: field_color }}
                                    mb={index === sendingOptions.length - 1 ? 0 : 4}
                                />
                            ))}
                        </Select>
                    </>
                    :
                    <>
                        <HStack justifyContent={'space-between'} alignItems={'center'}>
                            <Caption text='settings.communication_preferences' bold />
                            <Button
                                text={'general.edit'}
                                modifier={'small'}
                                onPress={() => setShowPreferredSelect(true)}
                            />
                        </HStack>
                        { member?.preferred_sending_method?.length > 0 &&
                        <Caption>
                            <Caption text={'settings.my_preferred'} />
                            <Caption bold>
                                {
                                    preferredOption.label.toLowerCase()
                                }
                            </Caption>
                        </Caption>
                        }
                    </>
                }
                { showAlert.status && showAlert.id === 'notification' &&
                    <Alert
                        type={showAlert.opt_state}
                        checkMessage={true}
                        signature={!showAlert.disable_signature}
                        setSignature={(sign) => setSignature(sign)}
                        title={showAlert.title || ''}
                        subHeaderText={t('settings.modal_paragraph', [
                            showAlert.disable_signature ? '' : 'and sign ',
                            showAlert.type
                        ])}
                        message={showAlert.disclaimer || ''}
                        setAcceptAlert={handleAcceptConditions}
                        setCancelAlert={handleCancelConditions}
                        showAlert={showAlert.status}
                        textConfirmButton='Opt in'
                    />
                }
                { showAlert.status && showAlert.id === 'closeNotification' &&
                    <Alert
                        title={showAlert.title}
                        message={showAlert.text}
                        setAcceptAlert={handleAcceptConditions}
                        setCancelAlert={handleCancelConditions}
                        showAlert={showAlert.status}
                    />
                }
            </Panel>
        </Section>
    );
};

export default SettingsScreen;
